import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Zap } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useApp } from "../context/AppContext";
import { Logo } from "../components/Logo";

function destFor(role) {
  if (role === "admin") return "/portal/admin";
  if (role === "artist") return "/portal/artist";
  return "/portal/customer";
}

export default function PortalLogin() {
  const { login, register, user } = useAuth();
  const { lang } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("login"); // 'login' | 'register'
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (user && user.role) {
      navigate(destFor(user.role), { replace: true });
    }
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = mode === "login"
      ? await login(form.email, form.password)
      : await register(form.email, form.password, form.name);
    setSubmitting(false);
    if (res.ok) {
      const dest = destFor(res.user.role);
      navigate(location.state?.from?.pathname || dest, { replace: true });
    } else {
      setError(res.error);
    }
  };

  const t = lang === "pt"
    ? {
        portal_tag: "/ PORTAL",
        login_title: "ENTRAR.",
        register_title: "CRIAR CONTA.",
        login_sub: "Artistas, wrestlers, admin — entra no teu painel. Clientes registam-se abaixo.",
        register_sub: "Cria a tua conta de cliente para acompanhar encomendas e contactar-nos.",
        login_tab: "ENTRAR",
        register_tab: "CRIAR CONTA",
        name: "NOME",
        email: "EMAIL",
        password: "PALAVRA-PASSE",
        password_hint: "Mín. 8 caracteres",
        submit_login: "ENTRAR",
        submit_register: "CRIAR CONTA",
        switch_to_login: "Já tens conta?",
        switch_to_register: "Novo aqui? Cria uma conta de cliente",
      }
    : {
        portal_tag: "/ PORTAL",
        login_title: "SIGN IN.",
        register_title: "CREATE ACCOUNT.",
        login_sub: "Artists, wrestlers, admin — log in to your dashboard. Customers register below.",
        register_sub: "Create your customer account to track orders and contact us.",
        login_tab: "SIGN IN",
        register_tab: "CREATE ACCOUNT",
        name: "NAME",
        email: "EMAIL",
        password: "PASSWORD",
        password_hint: "Min. 8 characters",
        submit_login: "ENTER",
        submit_register: "CREATE",
        switch_to_login: "Already have an account?",
        switch_to_register: "New here? Create a customer account",
      };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-5 py-12" data-testid="page-portal-login">
      <div className="w-full max-w-md">
        <Link to="/" className="block text-center mb-8">
          <Logo size={40} />
        </Link>
        <div className="border-2 border-kult-pink p-8 bg-kult-black2 kult-shadow-yellow">
          <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow">{t.portal_tag}</p>
          <h1 className="font-display text-5xl mt-2 leading-none">
            {mode === "login" ? t.login_title : t.register_title}
          </h1>
          <p className="text-kult-muted mt-2 text-sm">
            {mode === "login" ? t.login_sub : t.register_sub}
          </p>

          {/* Tabs */}
          <div className="flex gap-2 mt-6 border-b border-kult-border">
            <button
              type="button"
              onClick={() => { setMode("login"); setError(""); }}
              className={`px-4 py-2 font-mono text-[11px] tracking-[0.3em] ${mode === "login" ? "text-kult-pink border-b-2 border-kult-pink -mb-px" : "text-kult-muted"}`}
              data-testid="portal-tab-login"
            >
              {t.login_tab}
            </button>
            <button
              type="button"
              onClick={() => { setMode("register"); setError(""); }}
              className={`px-4 py-2 font-mono text-[11px] tracking-[0.3em] ${mode === "register" ? "text-kult-pink border-b-2 border-kult-pink -mb-px" : "text-kult-muted"}`}
              data-testid="portal-tab-register"
            >
              {t.register_tab}
            </button>
          </div>

          <form onSubmit={submit} className="mt-6 space-y-5" data-testid={`portal-${mode}-form`}>
            {mode === "register" && (
              <div>
                <label className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-2">{t.name}</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-transparent border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none"
                  data-testid="portal-register-name"
                />
              </div>
            )}
            <div>
              <label className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-2">{t.email}</label>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-transparent border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none"
                data-testid={`portal-${mode}-email`}
              />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-2">{t.password}</label>
              <input
                type="password"
                required
                minLength={mode === "register" ? 8 : undefined}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full bg-transparent border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none"
                data-testid={`portal-${mode}-password`}
              />
              {mode === "register" && (
                <p className="font-mono text-[10px] text-kult-muted mt-1">{t.password_hint}</p>
              )}
            </div>
            {error && (
              <p className="font-mono text-xs text-kult-pink" data-testid={`portal-${mode}-error`}>{error}</p>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-kult-pink text-black font-display text-2xl py-4 hover:bg-kult-yellow transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
              data-testid={`portal-${mode}-submit`}
            >
              <Zap size={20} /> {submitting ? "..." : (mode === "login" ? t.submit_login : t.submit_register)}
            </button>
          </form>

          <p className="text-center font-mono text-[11px] text-kult-muted mt-6">
            {mode === "login" ? (
              <button onClick={() => setMode("register")} className="text-kult-yellow hover:text-kult-pink" data-testid="switch-to-register">
                {t.switch_to_register}
              </button>
            ) : (
              <button onClick={() => setMode("login")} className="text-kult-yellow hover:text-kult-pink" data-testid="switch-to-login">
                {t.switch_to_login}
              </button>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
